import { useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { LayoutDashboard, ShoppingBag, Users, Car, BarChart3, X, HandCoins, FileSignature } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

// 跟電腦版側邊選單的「報表管理」一致：迅安簽單只有車行開啟該功能才顯示
const baseReportChildren = [
  { label: '司機報表', icon: Car,   path: '/reports/drivers'   },
  { label: '客戶報表', icon: Users, path: '/reports/customers' },
  { label: '廠商費用', icon: HandCoins, path: '/reports/vendors' },
]
const xunanReportChild = { label: '迅安簽單', icon: FileSignature, path: '/reports/xunan-sign' }

const isXunanSignEnabled = (): boolean => {
  try {
    const t = JSON.parse(localStorage.getItem('tenant') || '{}')
    const cfg = typeof t.form_config === 'string' ? JSON.parse(t.form_config || '{}') : (t.form_config || {})
    return !!cfg.enable_xunan_sign
  } catch {
    return false
  }
}

export default function BottomNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const [showReports, setShowReports] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const isDriver = user?.role === 'driver'
  const reportChildren = isXunanSignEnabled() ? [...baseReportChildren, xunanReportChild] : baseReportChildren
  const isReportsActive = isDriver
    ? location.pathname.startsWith('/my-report')
    : reportChildren.some(c => location.pathname.startsWith(c.path))

  useEffect(() => { setShowReports(false) }, [location.pathname])

  useEffect(() => {
    if (!showReports) return
    const fn = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowReports(false)
    }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [showReports])

  const coreItems = [
    { label: '儀表板', icon: LayoutDashboard, path: '/dashboard', key: 'dashboard' },
    { label: '訂單',   icon: ShoppingBag,     path: '/orders',    key: 'orders'    },
    ...(!isDriver ? [
      { label: '司機', icon: Car,        path: '/drivers',   key: 'drivers'   },
      { label: '客戶', icon: Users,      path: '/customers', key: 'customers' },
    ] : []),
  ]

  const handleReportsClick = () => {
    if (isDriver) {
      navigate('/my-report')
    } else {
      setShowReports(v => !v)
    }
  }

  return (
    <div ref={menuRef} className="lg:hidden">
      {/* Reports popup */}
      {showReports && (
        <div className="fixed bottom-16 left-0 right-0 z-50 mx-4 mb-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">報表管理</span>
            <button onClick={() => setShowReports(false)} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid divide-x divide-gray-100 dark:divide-gray-700" style={{ gridTemplateColumns: `repeat(${reportChildren.length > 3 ? 2 : reportChildren.length}, minmax(0, 1fr))` }}>
            {reportChildren.map(child => {
              const Icon = child.icon
              const isActive = location.pathname.startsWith(child.path)
              return (
                <button
                  key={child.path}
                  onClick={() => navigate(child.path)}
                  className={clsx(
                    'flex flex-col items-center gap-2 py-4 transition-colors',
                    isActive
                      ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                  )}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-sm font-medium">{child.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 z-50 w-full h-16 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div
          className="grid h-full max-w-lg mx-auto font-medium"
          style={{ gridTemplateColumns: `repeat(${coreItems.length + 1}, minmax(0, 1fr))` }}
        >
          {coreItems.map(item => {
            const Icon = item.icon
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
            return (
              <button key={item.key} type="button" onClick={() => navigate(item.path)}
                className={clsx('inline-flex flex-col items-center justify-center px-5 transition-colors duration-200',
                  isActive ? 'text-blue-600 dark:text-blue-500' : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
                )}
              >
                <Icon className="w-6 h-6 mb-1" />
                <span className="text-xs">{item.label}</span>
              </button>
            )
          })}
          <button type="button" onClick={handleReportsClick}
            className={clsx('inline-flex flex-col items-center justify-center px-5 transition-colors duration-200',
              (isReportsActive || showReports) ? 'text-blue-600 dark:text-blue-500' : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
            )}
          >
            <BarChart3 className="w-6 h-6 mb-1" />
            <span className="text-xs">報表</span>
          </button>
        </div>
      </div>
    </div>
  )
}
